import { useDirectusStore } from "@/modules/directus/store/directus";
import {
  AuthenticationDetails,
  CodeDeliveryDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  ISignUpResult,
} from "amazon-cognito-identity-js";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import {
  createItem,
  customEndpoint,
  readItems,
  updateItem,
  uploadFiles,
} from "@directus/sdk";
import { useCityStore } from "@/modules/city/store/city";
import CityInterface from "@/modules/city/interfaces/CityInterface";
import ProfileInterface from "@/modules/profile/interfaces/ProfileInterface";
import { trackEvent } from "@/plugins/analytics";

export const useAuthStore = defineStore("auth", () => {
  const directusStore = useDirectusStore();
  const session = ref<CognitoUserSession | null>(null);

  const Pool = new CognitoUserPool({
    UserPoolId: process.env.VUE_APP_SSO_POOL_ID as string,
    ClientId: process.env.VUE_APP_SSO_CLIENT_ID as string,
  });

  const profile = ref<null | ProfileInterface>(null);

  const isAuthenticated = computed(() => {
    return !!session.value;
  });

  const cityStore = useCityStore();

  watch(
    () => cityStore.city,
    (newCity: CityInterface | null) => {
      if (newCity) {
        authenticateDirectus();
      }
    }
  );

  const authenticateDirectus = async (token?: string) => {
    if (!isAuthenticated.value) {
      return;
    }

    token = token || session.value?.getIdToken().getJwtToken();

    const mainClientResponse = await directusStore.main.request<any>(
      customEndpoint({
        path: "/directus-extension-cognito-sso",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      })
    );

    directusStore.storages.main.set({
      ...directusStore.storages.main.get(),
      refresh_token: mainClientResponse.refreshToken,
    });
    directusStore.main.refresh();

    if (directusStore.city) {
      const city = cityStore.city as CityInterface;

      const cityClientResponse = await directusStore.city.request<any>(
        customEndpoint({
          path: "/directus-extension-cognito-sso",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token }),
        })
      );

      directusStore.storages[city.id].set({
        ...directusStore.storages[city.id].get(),
        refresh_token: cityClientResponse.refreshToken,
      });
      directusStore.city.refresh();
    }

    return Promise.resolve(mainClientResponse);
  };

  const init = () => {
    Pool.getCurrentUser()?.getSession(
      async (_err: Error | null, cognitoSession: CognitoUserSession | null) => {
        session.value = cognitoSession;

        if (session.value?.isValid()) {
          await authenticateDirectus(session.value.getIdToken().getJwtToken());
        }
      }
    );
  };

  init();

  const login = (credentials: { Username: string; Password: string }) => {
    const user = new CognitoUser({ Username: credentials.Username, Pool });

    return new Promise((resolve, reject) => {
      user.authenticateUser(new AuthenticationDetails(credentials), {
        onSuccess: (cognitoSession) => {
          session.value = cognitoSession;
          authenticateDirectus(cognitoSession.getIdToken().getJwtToken())
            .then(async () => {
              trackEvent("login", {
                method: credentials.Username.includes("@") ? "email" : "phone",
              });
              resolve(session.value);
            })
            .catch((error) => {
              session.value = null;
              reject(error);
            });
        },
        onFailure: (error) => {
          reject(error);
        },
      });
    });
  };

  const signUp = (user: {
    Email?: string;
    Phone?: string;
    Nickname: string;
    Password: string;
  }): Promise<ISignUpResult> => {
    const attributesList = [
      new CognitoUserAttribute({
        Name: "nickname",
        Value: user.Nickname,
      }),
    ];
    const userData: ProfileInterface = {
      id: "",
      username: user.Nickname,
      sub: "" as string,
    };
    if (user.Email) {
      attributesList.push(
        new CognitoUserAttribute({
          Name: "email",
          Value: user.Email,
        })
      );
      userData.email = user.Email;
    }
    if (user.Phone) {
      attributesList.push(
        new CognitoUserAttribute({
          Name: "phone_number",
          Value: user.Phone,
        })
      );
      userData.phone = user.Phone;
    }

    return new Promise((resolve, reject) => {
      Pool.signUp(
        (user.Email || user.Phone) as string,
        user.Password,
        attributesList,
        [],
        (err, result) => {
          if (err) {
            reject(err);
          } else {
            trackEvent("register", {
              method: user.Email ? "email" : "phone",
            });
            resolve(result as ISignUpResult);
          }
        }
      );
    });
  };

  const confirmRegistration = (Username: string, code: string) => {
    const user = new CognitoUser({
      Username,
      Pool,
    });
    return new Promise((resolve, reject) => {
      user.confirmRegistration(code, true, (error, result) => {
        if (error) {
          reject(error);
        } else {
          trackEvent("register_confirmed");
          resolve(result);
        }
      });
    });
  };

  const resendCode = (
    Username: string
  ): Promise<{ CodeDeliveryDetails: CodeDeliveryDetails }> => {
    const user = new CognitoUser({
      Username,
      Pool,
    });
    return new Promise((resolve, reject) => {
      user.resendConfirmationCode((error, result) => {
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      });
    });
  };

  const logout = async () => {
    const logoutPromises = [];
    if (await directusStore.main.getToken()) {
      logoutPromises.push(directusStore.main.logout());
    }
    if (await directusStore.city?.getToken()) {
      logoutPromises.push(directusStore.city?.logout());
    }

    return Promise.all(logoutPromises).then(() => {
      return new Promise<void>((resolve) => {
        Pool.getCurrentUser()?.signOut(() => {
          session.value = null;
          resolve();
        });
      });
    });
  };

  const forgotPassword = (
    Username: string
  ): Promise<{ CodeDeliveryDetails: CodeDeliveryDetails }> => {
    const user = new CognitoUser({
      Username,
      Pool,
    });
    return new Promise((resolve, reject) => {
      user.forgotPassword({
        onSuccess: (codeDeliveryDetails) => {
          resolve(codeDeliveryDetails);
        },
        onFailure: (error) => {
          reject(error);
        },
      });
    });
  };

  const confirmPassword = (
    Code: string,
    Username: string,
    Password: string
  ) => {
    const user = new CognitoUser({
      Username,
      Pool,
    });
    return new Promise<void>((resolve, reject) => {
      user.confirmPassword(Code, Password, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (error) => {
          reject(error);
        },
      });
    });
  };

  const changePassword = (PreviousPassword: string, Password: string) => {
    const user = Pool.getCurrentUser();
    if (user) {
      return new Promise((resolve, reject) => {
        user.getSession((error: Error) => {
          if (error) {
            reject(error);
          } else {
            user.changePassword(PreviousPassword, Password, (error, result) => {
              if (error) {
                reject(error);
              } else {
                resolve(result);
              }
            });
          }
        });
      });
    }
    return Promise.reject(new Error("Unexpected error"));
  };

  const fetchUserByCogsub = (sub: string) => {
    return directusStore.main
      .request(
        readItems("users", {
          filter: {
            sub: {
              _eq: sub,
            },
          },
        })
      )
      .then((response) => {
        if (response.length) {
          return response.shift();
        } else {
          return null;
        }
      });
  };

  const updateUser = (
    sub: string,
    updateData: Omit<ProfileInterface, "id" | "sub">,
    imageFile: File | null
  ) => {
    return fetchUserByCogsub(sub).then(async (user) => {
      // Upload image if file is provided
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        const uploadResponse = await directusStore.main.request(
          uploadFiles(formData)
        );
        if (uploadResponse) {
          updateData.picture = uploadResponse.id;
        }
      }
      if (!user) {
        return directusStore.main
          .request(createItem("users", updateData))
          .then(async (response) => {
            profile.value = (await fetchUserByCogsub(sub)) as ProfileInterface;

            return response;
          });
      } else {
        return directusStore.main
          .request(updateItem("users", user.id, updateData))
          .then(async (response) => {
            profile.value = (await fetchUserByCogsub(sub)) as ProfileInterface;

            return response;
          });
      }
    });
  };

  return {
    profile,
    isAuthenticated,
    Pool,
    login,
    logout,
    changePassword,
    confirmPassword,
    confirmRegistration,
    signUp,
    forgotPassword,
    resendCode,
    fetchUserByCogsub,
    updateUser,
  };
});
